/* eslint-disable camelcase */
import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import clsx from 'clsx';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { gsap } from 'gsap';
import useScreen from 'hooks/useScreen';

import useStyles from './useStyles';
import PageNav from './components/PageNav';
import mob1_2x from './images/mob1_2x.png';
import mob1_3x from './images/mob1_3x.png';
import web1_2x from './images/web1_2x.png';
import web1_3x from './images/web1_3x.png';
import mob2_2x from './images/mob2_2x.png';
import mob2_3x from './images/mob2_3x.png';
import web2_2x from './images/web2_2x.png';
import web2_3x from './images/web2_3x.png';
import mob3_2x from './images/mob3_2x.png';
import mob3_3x from './images/mob3_3x.png';
import web3_2x from './images/web3_2x.png';
import web3_3x from './images/web3_3x.png';

const Description = ({ data }) => {
  gsap.registerPlugin(ScrollTrigger);

  const classes = useStyles();
  const { t } = useTranslation();
  const { isDesktop } = useScreen();

  const sectionRefs = useRef([]);
  const addToSectionRefs = el => {
    if (el && !sectionRefs.current.includes(el)) {
      sectionRefs.current.push(el);
    }
  };

  useEffect(() => {
    let listOfAnimation = [];
    if (isDesktop) {
      listOfAnimation = sectionRefs.current.reduce((acc, item) => {
        const { id } = item;

        const image = item.querySelector(`#${id}-image`);
        const number = item.querySelector(`#${id}-number`);
        const text = item.querySelector(`#${id}-text`);

        const animation = gsap.fromTo(
          [image, number, text],
          {
            opacity: 0,
          },
          {
            duration: 0.4,
            opacity: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: item,
              start: 'top 65%',
              toggleActions: 'play none none reverse',
            },
          },
        );
        return [...acc, animation];
      }, []);
    }
    return () => listOfAnimation.forEach(item => item.scrollTrigger.disable(false));
  }, []);

  const [firstSection, secondSection, thirdSection] = data;

  const pageNavItems = [
    {
      id: 'anchor-3',
      label: thirdSection.link,
      href: '#section-3',
      offsetY: 100,
    },
    {
      id: 'anchor-2',
      label: secondSection.link,
      href: '#section-2',
    },
    {
      id: 'anchor-1',
      label: firstSection.link,
      href: '#section-1',
    },
  ];

  return (
    <div className={classes.container} id="how-it-works">
      <div className={classes.sectionFirst} id="section-1" ref={addToSectionRefs}>
        <div className={clsx(classes.sectionContent, classes.sectionFirstContent)}>
          <Typography variant="h3" className={classes.title}>
            {t('components.NonAuthHomePage.Description.title')}
          </Typography>
          <div className={classes.pageNav}>
            <PageNav items={pageNavItems} />
          </div>
          <div className={classes.text}>
            <Typography className={classes.sectionNumber} variant="h2" id="section-1-number">
              01
            </Typography>
            <div id="section-1-text">
              <Typography className={classes.sectionTitle} variant="h6">
                {firstSection.title}
              </Typography>
              <Typography
                className={classes.sectionDescription}
                dangerouslySetInnerHTML={{
                  __html: firstSection.description,
                }}
              />
            </div>
          </div>
          <picture>
            <source media="(max-width: 766px)" srcSet={`${mob1_2x} 2x, ${mob1_3x} 3x`} />
            <source media="(min-width: 767px)" srcSet={`${web1_2x} 2x, ${web1_3x} 3x`} />
            <img
              src={`${web1_2x}`}
              alt=""
              className={clsx(classes.sectionImage, classes.sectionFirstImage)}
              id="section-1-image"
            />
          </picture>
        </div>
      </div>
      <div className={classes.sectionSecond} id="section-2" ref={addToSectionRefs}>
        <div className={clsx(classes.sectionContent, classes.sectionSecondContent)}>
          <div className={classes.pageNav}>
            <PageNav items={pageNavItems} />
          </div>
          <div className={classes.text}>
            <Typography
              className={clsx(classes.sectionNumber, classes.sectionSecondNumber)}
              variant="h2"
              id="section-2-number"
            >
              02
            </Typography>
            <div id="section-2-text">
              <Typography className={classes.sectionTitle} variant="h6">
                {secondSection.title}
              </Typography>
              <Typography
                className={classes.sectionDescription}
                dangerouslySetInnerHTML={{
                  __html: secondSection.description,
                }}
              />
            </div>
          </div>
          <picture>
            <source media="(max-width: 766px)" srcSet={`${mob2_2x} 2x, ${mob2_3x} 3x`} />
            <source media="(min-width: 767px)" srcSet={`${web2_2x} 2x, ${web2_3x} 3x`} />
            <img
              src={`${web2_2x}`}
              alt=""
              className={clsx(classes.sectionSecondImage, classes.sectionImage)}
              id="section-2-image"
            />
          </picture>
        </div>
      </div>
      <div className={classes.sectionThird} id="section-3" ref={addToSectionRefs}>
        <div className={clsx(classes.sectionContent, classes.sectionThirdContent)}>
          <div className={clsx(classes.pageNav, classes.pageNavWithBigSpaceBottom)}>
            <PageNav items={pageNavItems} light />
          </div>
          <div className={clsx(classes.text, classes.sectionThirdText)}>
            <Typography className={classes.sectionNumber} variant="h2" id="section-3-number">
              03
            </Typography>
            <div id="section-3-text">
              <Typography className={clsx(classes.sectionTitle, classes.light)} variant="h6">
                {thirdSection.title}
              </Typography>
              <Typography
                className={clsx(classes.sectionDescription, classes.light)}
                dangerouslySetInnerHTML={{
                  __html: thirdSection.description,
                }}
              />
            </div>
          </div>
          <picture>
            <source media="(max-width: 766px)" srcSet={`${mob3_2x} 2x, ${mob3_3x} 3x`} />
            <source media="(min-width: 767px)" srcSet={`${web3_2x} 2x, ${web3_3x} 3x`} />
            <img
              src={`${web3_2x}`}
              alt=""
              className={clsx(classes.sectionThirdImage, classes.sectionImage)}
              id="section-3-image"
            />
          </picture>
        </div>
      </div>
    </div>
  );
};

export default Description;



// WEBPACK FOOTER //
// ./src/components/NonAuthHomePage/components/Description/Description.js